/**
 * 应用启动初始化模块
 * 统一处理 favicon、Sentry、Beacon、Aegis 的初始化逻辑
 */
import * as Sentry from '@sentry/react';
import BeaconAction from '@tencent/beacon-web-sdk';
import Aegis from '@tencent/aegis-web-sdk';
import { getFavicon, getFeatureFlags, getTextConfig } from './index';

// Sentry DSN 配置（按项目区分）
const SENTRY_DSN_MAP: Record<string, string> = {
  capturer: 'https://5b4345d56878b05fadd2d536a02d7131@o4505871093989376.ingest.sentry.io/4505899150606336',
  receiver: 'https://172fe30496641cc9d2eea15725e2ddd0@o4505871093989376.ingest.sentry.io/4506114496069632',
  console: 'https://58e2be68267a972ad3859a0114caf2df@o4505871093989376.ingest.sentry.io/4506114590900224',
};

// Beacon 配置
const BEACON_CONFIG = {
  appkey: '0WEB05J7AGNBBYIQ',
  strictMode: false,
  delay: 1000,
  isOversea: false,
  needQueryConfig: true,
};

// Aegis 配置
const AEGIS_CONFIG = {
  id: 'sAAONGXvMsBzORpMBd',
  reportApiSpeed: true,
  reportAssetSpeed: true,
  spa: true,
  reportImmediately: true,
};

// Sentry 通用忽略错误
const SENTRY_IGNORE_ERRORS = [
  'TypeError: Failed to fetch',
  'TypeError: NetworkError when attempting to fetch resource.',
  'TypeError: Cancelled',
  'Non-Error promise rejection captured with keys: errors',
  'AbortError: The operation was aborted.',
];

export type AppType = 'capturer' | 'receiver' | 'console';

export interface BootstrapOptions {
  /** 应用类型 */
  app: AppType;
  /** 是否开发环境 */
  isDev?: boolean;
  /** 版本号 */
  version?: string;
  /** 环境名称 */
  environment?: string;
}

/**
 * 设置 Favicon
 */
export const setupFavicon = (): void => {
  const faviconLink = document.getElementById('favicon') as HTMLLinkElement;
  if (faviconLink) {
    faviconLink.href = getFavicon();
  }
};

/**
 * 初始化 Sentry
 */
export const initSentry = (options: BootstrapOptions): void => {
  const dsn = SENTRY_DSN_MAP[options.app];
  if (!dsn) {
    console.warn(`[Bootstrap] Unknown app type: ${options.app}, skipping Sentry init`);
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: ['localhost'],
      }),
      new Sentry.Replay(),
    ],
    environment: options.environment,
    release: options.version,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: SENTRY_IGNORE_ERRORS,
  });
};

/**
 * 初始化 Beacon 数据埋点
 */
export const initBeacon = (options: BootstrapOptions): void => {
  const featureFlags = getFeatureFlags();
  if (!featureFlags.enableBeacon) {
    return;
  }

  window.beacon = new BeaconAction({
    ...BEACON_CONFIG,
    versionCode: options.version,
  });
};

/**
 * 初始化 Aegis 性能监控
 */
export const initAegis = (options: BootstrapOptions): void => {
  const featureFlags = getFeatureFlags();
  if (!featureFlags.enableAegis) {
    return;
  }

  window.aegis = new Aegis({
    ...AEGIS_CONFIG,
    env: options.environment,
    uin: options.app,  // 标记端类型：capturer / receiver / console（后续 setConfig 覆盖为实际用户 ID）
  });
};

/**
 * 设置页面标题
 */
export const setupTitle = (): void => {
  document.title = getTextConfig().appTitle;
};

/**
 * 应用启动初始化
 * 统一执行 favicon、Sentry、Beacon、Aegis 的初始化
 */
export const bootstrap = (options: BootstrapOptions): void => {
  const isDev = options.isDev ?? import.meta.env.MODE === 'development';
  const version = options.version ?? import.meta.env.RELEASE_VERSION;
  const environment = options.environment ?? import.meta.env.MODE;

  const fullOptions: BootstrapOptions = {
    ...options,
    isDev,
    version,
    environment,
  };

  // 设置 Favicon
  setupFavicon();

  // 设置页面标题
  setupTitle();

  // 开发环境不初始化监控
  // if (isDev) {
  //   return;
  // }

  // 初始化 Sentry
  initSentry(fullOptions);

  // 初始化 Beacon
  initBeacon(fullOptions);

  // 初始化 Aegis（capturer / receiver / console；受 featureFlags.enableAegis 环境开关控制）
  if (options.app === 'receiver' || options.app === 'capturer' || options.app === 'console') {
    initAegis(fullOptions);
  }
};
